import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from './PortfolioTiles.module.scss';
import KpiCard from '../ReportMetrics/widgets/KpiCard';
import LastComputed from '../ReportMetrics/widgets/LastComputed';
import { formatPercent } from '../ReportMetrics/intelligence';
import type { PortfolioSummary } from '../ReportMetrics/portfolio';
import type { KpiDatum } from '../ReportMetrics/reportData';

interface PortfolioTilesProps {
  summary: PortfolioSummary;
  /** Oldest recompute across the portfolio - how current these tiles really are. */
  computedAt?: string;
}

/**
 * The Dashboard's KPI band (spec §17, p. 35): how the whole portfolio is doing,
 * above the project list.
 *
 * Deliberately reuses the Project Intelligence `KpiCard` rather than restyling
 * a tile here - the same number should look the same wherever it appears, and
 * the Cockpit already does this.
 *
 * Health bands are the engine's four (`ph.project_health_status`), not the
 * mockup's three: collapsing At Risk into Critical would report a project as
 * worse than the engine says it is.
 */
const PortfolioTiles: React.FC<PortfolioTilesProps> = ({ summary, computedAt }) => {
  const { t } = useTranslation('dashboard');

  const tiles: KpiDatum[] = [
    {
      key: 'totalProjects',
      value: `${summary.total}`,
      delta: '—',
      direction: 'flat',
      higherIsBetter: true,
    },
    {
      key: 'averageHealth',
      value: formatPercent(summary.averageHealth),
      delta: '—',
      direction: 'flat',
      higherIsBetter: true,
    },
    {
      key: 'onTrack',
      value: `${summary.byHealth.onTrack}`,
      delta: '—',
      direction: 'flat',
      higherIsBetter: true,
    },
    {
      key: 'watch',
      value: `${summary.byHealth.watch}`,
      delta: '—',
      direction: 'flat',
      higherIsBetter: false,
    },
    {
      key: 'atRisk',
      value: `${summary.byHealth.atRisk}`,
      delta: '—',
      direction: 'flat',
      higherIsBetter: false,
    },
    {
      key: 'critical',
      value: `${summary.byHealth.critical}`,
      delta: '—',
      direction: 'flat',
      higherIsBetter: false,
    },
    {
      key: 'openAlerts',
      value: `${summary.openAlerts}`,
      delta: '—',
      direction: 'flat',
      higherIsBetter: false,
    },
  ];

  return (
    <>
      <div className={styles.tiles}>
        {tiles.map((datum) => (
          <KpiCard
            key={datum.key}
            label={t(`portfolioTiles.${datum.key}.label`)}
            hint={t(`portfolioTiles.${datum.key}.hint`)}
            datum={datum}
          />
        ))}
      </div>
      {/* recomputes run on a queue, so the tiles say how old they are */}
      <div className={styles.freshness}>
        <LastComputed at={computedAt} scope="portfolio" />
      </div>
    </>
  );
};

export default PortfolioTiles;
